import { Signal } from '@latitude/signal';
import { Spinner } from '@latitude/spinner';
import { cn } from '@latitude/token-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { getSpinnerSize, getSpinnerVariant } from 'helpers';
import type { ComponentRef, Ref } from 'react';
import { useMemo } from 'react';
import type {
  IconButtonProps,
  IconButtonSize,
  IconButtonType,
  IconButtonVariant,
} from 'types';

import { iconButtonVariants, iconVariants } from './variants';

/**
 * An icon-only button.
 *
 * @example
 * <IconButton icon={<CloseIcon />} aria-label="Close" />
 */
const IconButton = ({
  className,
  icon,
  variant = 'primary',
  size = 'medium',
  type = 'filled',
  disabled = false,
  loading = false,
  asChild = false,
  selected = false,
  onClick,
  signal,
  htmlType,
  rawProps = {},
  children,
  'aria-label': ariaLabel,
  'data-testid': testID = 'icon-button-test',
  ref,
}: IconButtonProps & {
  ref?: Ref<ComponentRef<'button'>>;
}) => {
  const Comp = asChild ? Slot : 'button';
  const defaultHtmlType = asChild ? undefined : 'button';

  const iconButtonVariantsClasses: string = useMemo(
    () =>
      iconButtonVariants({
        variant,
        type,
        size,
        disabled,
        loading,
        selected,
      }),
    [variant, type, size, disabled, selected, loading],
  );

  return (
    <Comp
      ref={ref}
      className={cn('relative', iconButtonVariantsClasses, className)}
      data-loading={loading}
      data-testid={testID}
      tabIndex={loading || disabled ? -1 : 0}
      disabled={disabled || loading}
      onClick={onClick}
      type={htmlType ?? defaultHtmlType}
      aria-label={ariaLabel}
      {...rawProps}
    >
      <Slottable>{children}</Slottable>
      <span
        className={cn('pointer-events-none', iconVariants({ size, loading }))}
      >
        {icon}
      </span>
      {loading && (
        <Spinner
          size={getSpinnerSize(size)}
          className="pointer-events-none absolute inset-0 m-auto"
          variant={getSpinnerVariant(variant, type)}
        />
      )}
      {signal && (
        <Signal
          {...signal}
          border
          className={cn([
            'pointer-events-none',
            'absolute',
            '-right-sizing-sys-size-4',
            '-top-sizing-sys-size-4',
          ])}
        />
      )}
    </Comp>
  );
};

IconButton.displayName = 'IconButton';

export {
  IconButton,
  iconButtonVariants,
  type IconButtonProps,
  type IconButtonSize,
  type IconButtonType,
  type IconButtonVariant,
};
